import {
  type TextAnswer,
  type TextQuestion,
} from '@/features/jobs/questions/questions'
import { QuestionAnswerEditorViewModelBase } from '@/features/jobs/questions/QuestionAnswerEditorViewModelBase'
import { action, computed, observable } from 'mobx'
import { QuestionValidationResult } from '@/features/jobs/questions/question-validation-result'

export class TextQuestionAnswerEditorViewModel extends QuestionAnswerEditorViewModelBase<TextQuestion> {
  readonly type = 'Text'

  /**
   * Actual committed value of the text answer.
   */
  @observable accessor committedValue = ''
  @observable accessor editingValue = ''

  /** @inheritDoc */
  @computed get isAnswered() {
    return this.committedValue.trim().length > 0
  }

  /** @inheritDoc */
  @computed get answer(): TextAnswer | null {
    const trimmedValue = this.committedValue.trim()
    return trimmedValue.length > 0
      ? { type: 'Text', value: trimmedValue }
      : null
  }

  /** @inheritDoc */
  @computed get textAnswer(): string | null {
    const trimmedValue = this.committedValue.trim()
    return trimmedValue.length > 0 ? trimmedValue : null
  }

  /**
   * Sets the value.
   *
   * @param value
   */
  @action.bound
  setEditingText(value: string) {
    this.editingValue = value
  }

  /** @inheritDoc */
  validate(): QuestionValidationResult {
    if (!this.isAnswered) {
      return QuestionValidationResult.failed(['Please enter your answer'])
    }
    return QuestionValidationResult.ok()
  }

  /** @inheritDoc */
  @action.bound
  set(question: TextQuestion) {
    const value = question.answer ? question.answer.value : ''
    this.committedValue = value
    this.editingValue = value
  }

  /**
   * Commits the current editing value and notifies of answer change.
   */
  @action.bound
  flush() {
    const value = this.editingValue.trim()
    const isDirty = this.committedValue !== value
    this.committedValue = value
    if (isDirty) {
      this.notifyAnswerChanged()
    }
  }
}
